import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { DataPagination } from '@/components/DataPagination';
import { format } from 'date-fns';
import { Search, Download, Eye, Loader2, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';

const ACTION_COLORS: Record<string, string> = {
  CREATE: 'bg-green-100 text-green-800 border-green-200',
  UPDATE: 'bg-blue-100 text-blue-800 border-blue-200',
  DELETE: 'bg-red-100 text-red-800 border-red-200',
  UPSERT: 'bg-purple-100 text-purple-800 border-purple-200',
};

export default function AuditLog() {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState<string>('all');
  const [tableFilter, setTableFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selectedLog, setSelectedLog] = useState<any | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [sortColumn, setSortColumn] = useState<string>('created_at');
  const [sortAsc, setSortAsc] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setCurrentPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data: auditData, isLoading } = useQuery({
    queryKey: ['audit-logs', currentPage, pageSize, search, actionFilter, tableFilter, sortColumn, sortAsc],
    queryFn: async () => {
      let query = supabase
        .from('admin_audit_log')
        .select('*', { count: 'exact' });

      if (search) {
        query = query.or(`admin_email.ilike.%${search}%,record_id.ilike.%${search}%`);
      }

      if (actionFilter !== 'all') {
        query = query.eq('action_type', actionFilter);
      }

      if (tableFilter !== 'all') {
        query = query.eq('table_name', tableFilter);
      }

      query = query
        .order(sortColumn, { ascending: sortAsc })
        .range((currentPage - 1) * pageSize, currentPage * pageSize - 1);

      const { data, count, error } = await query;
      if (error) throw error;
      return { data: data ?? [], total: count ?? 0 };
    },
  });

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortAsc((prev) => !prev);
    } else {
      setSortColumn(column);
      setSortAsc(false);
    }
    setCurrentPage(1);
  };

  const SortIcon = ({ column }: { column: string }) => {
    if (sortColumn !== column) return <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />;
    return sortAsc
      ? <ArrowUp className="ml-1 h-3 w-3" />
      : <ArrowDown className="ml-1 h-3 w-3" />;
  };

  const logs = auditData?.data ?? [];
  const totalLogs = auditData?.total ?? 0;
  const totalPages = Math.ceil(totalLogs / pageSize);

  // Get unique table names for filter
  const { data: tableNames } = useQuery({
    queryKey: ['audit-log-tables'],
    queryFn: async () => {
      const { data } = await supabase
        .from('admin_audit_log')
        .select('table_name');
      
      if (!data) return [];
      
      const unique = [...new Set(data.map((d: any) => d.table_name))];
      return unique.sort() as string[];
    },
  });

  const handleExport = async () => {
    setIsExporting(true);
    try {
      let query = supabase
        .from('admin_audit_log')
        .select('*');

      if (search) {
        query = query.or(`admin_email.ilike.%${search}%,record_id.ilike.%${search}%`);
      }

      if (actionFilter !== 'all') {
        query = query.eq('action_type', actionFilter);
      }

      if (tableFilter !== 'all') {
        query = query.eq('table_name', tableFilter);
      }

      query = query.order(sortColumn, { ascending: sortAsc });

      const { data: exportData, error } = await query;

      if (error) throw error;
      if (!exportData || exportData.length === 0) {
        toast.error('No audit logs available to export');
        return;
      }

      const columns: string[] = Array.from(
        exportData.reduce((keys, row) => {
          Object.keys(row).forEach((key) => keys.add(key));
          return keys;
        }, new Set<string>())
      );

      const escapeCsvValue = (value: any) => {
        const stringValue = value == null
          ? ''
          : typeof value === 'object'
            ? JSON.stringify(value)
            : String(value);
        return `"${stringValue.replace(/"/g, '""')}"`;
      };

      const csvRows = [
        columns.join(','),
        ...exportData.map((row) => columns.map((column) => escapeCsvValue(row[column])).join(',')),
      ];

      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit-log-export-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Exported ${exportData.length} audit log entries`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to export audit logs');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Admin Audit Log</h1>
        <Button variant="outline" onClick={handleExport} disabled={isExporting || !logs || logs.length === 0}>
          <Download className="mr-2 h-4 w-4" />
          {isExporting ? 'Exporting...' : 'Export'}
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl sm:text-2xl">Filters</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            {/* Search */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Email or Record ID..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            {/* Action Type Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Action Type</label>
              <Select
                value={actionFilter}
                onValueChange={(v) => { setCurrentPage(1); setActionFilter(v); }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  <SelectItem value="CREATE">Create</SelectItem>
                  <SelectItem value="UPDATE">Update</SelectItem>
                  <SelectItem value="DELETE">Delete</SelectItem>
                  <SelectItem value="UPSERT">Upsert</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Table Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Table</label>
              <Select
                value={tableFilter}
                onValueChange={(v) => { setCurrentPage(1); setTableFilter(v); }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tables</SelectItem>
                  {tableNames?.map((table) => (
                    <SelectItem key={table} value={table}>
                      {table}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Audit Log Table */}
      <Card>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <button className="flex items-center font-medium hover:text-foreground" onClick={() => handleSort('created_at')}>
                    Timestamp<SortIcon column="created_at" />
                  </button>
                </TableHead>
                <TableHead>
                  <button className="flex items-center font-medium hover:text-foreground" onClick={() => handleSort('admin_email')}>
                    Admin<SortIcon column="admin_email" />
                  </button>
                </TableHead>
                <TableHead>
                  <button className="flex items-center font-medium hover:text-foreground" onClick={() => handleSort('action_type')}>
                    Action<SortIcon column="action_type" />
                  </button>
                </TableHead>
                <TableHead>
                  <button className="flex items-center font-medium hover:text-foreground" onClick={() => handleSort('table_name')}>
                    Table<SortIcon column="table_name" />
                  </button>
                </TableHead>
                <TableHead>Record ID</TableHead>
                <TableHead>Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    No audit logs found
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log: any) => (
                  <TableRow key={log.id} className="cursor-pointer hover:bg-muted/50" onClick={() => setSelectedLog(log)}>
                    <TableCell className="whitespace-nowrap">
                      {format(new Date(log.created_at), 'MMM d, yyyy h:mm:ss a')}
                    </TableCell>
                    <TableCell>{log.admin_email}</TableCell>
                    <TableCell>
                      <Badge className={ACTION_COLORS[log.action_type] || ''}>
                        {log.action_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{log.table_name}</TableCell>
                    <TableCell className="font-mono text-xs">{log.record_id || '—'}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" className="h-7 w-7">
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          {logs && logs.length > 0 && (
            <DataPagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={totalLogs}
              onPageChange={setCurrentPage}
              onPageSizeChange={(newSize) => { setCurrentPage(1); setPageSize(newSize); }}
            />
          )}
        </CardContent>
      </Card>

      {/* Detail Drawer */}
      <Sheet open={!!selectedLog} onOpenChange={(open) => !open && setSelectedLog(null)}>
        <SheetContent className="overflow-y-auto w-full sm:max-w-2xl">
          <SheetHeader>
            <SheetTitle>Audit Log Details</SheetTitle>
          </SheetHeader>
          {selectedLog && (
            <div className="space-y-6 mt-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Timestamp</p>
                  <p className="font-medium">{format(new Date(selectedLog.created_at), 'PPpp')}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Admin User</p>
                  <p className="font-medium">{selectedLog.admin_email}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Action Type</p>
                  <Badge className={ACTION_COLORS[selectedLog.action_type] || ''}>
                    {selectedLog.action_type}
                  </Badge>
                </div>
                <div>
                  <p className="text-muted-foreground">Table</p>
                  <p className="font-mono text-xs">{selectedLog.table_name}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-muted-foreground">Record ID</p>
                  <p className="font-mono text-xs">{selectedLog.record_id || 'N/A'}</p>
                </div>
              </div>

              {selectedLog.changes && Object.keys(selectedLog.changes).length > 0 && (
                <div>
                  <p className="text-sm font-semibold mb-2">Changes</p>
                  <div className="border rounded-lg p-3 bg-muted/30 space-y-2">
                    {Object.entries(selectedLog.changes).map(([key, value]: [string, any]) => (
                      <div key={key} className="text-xs space-y-1">
                        <p className="font-medium">{key}</p>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="bg-red-50 p-2 rounded border border-red-200">
                            <p className="text-red-600 font-semibold mb-1">Old:</p>
                            <pre className="whitespace-pre-wrap break-all">{JSON.stringify(value.old, null, 2)}</pre>
                          </div>
                          <div className="bg-green-50 p-2 rounded border border-green-200">
                            <p className="text-green-600 font-semibold mb-1">New:</p>
                            <pre className="whitespace-pre-wrap break-all">{JSON.stringify(value.new, null, 2)}</pre>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedLog.old_values && (
                <div>
                  <p className="text-sm font-semibold mb-2">Old Values</p>
                  <pre className="border rounded-lg p-3 bg-muted/30 text-xs overflow-auto max-h-64">
                    {JSON.stringify(selectedLog.old_values, null, 2)}
                  </pre>
                </div>
              )}

              {selectedLog.new_values && (
                <div>
                  <p className="text-sm font-semibold mb-2">New Values</p>
                  <pre className="border rounded-lg p-3 bg-muted/30 text-xs overflow-auto max-h-64">
                    {JSON.stringify(selectedLog.new_values, null, 2)}
                  </pre>
                </div>
              )}

              {/* Why it happened, and anything the writer recorded beyond the
                  before/after. An email change puts the admin's stated reason
                  here, plus which tables it moved and what it revoked — all of
                  which were invisible on this page until now, because only
                  old_values/new_values were rendered. */}
              {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                <div>
                  <p className="text-sm font-semibold mb-2">Context</p>
                  {selectedLog.metadata.reason && (
                    <p className="mb-2 border-l-2 border-muted-foreground/30 pl-2 text-xs italic text-muted-foreground">
                      {String(selectedLog.metadata.reason)}
                    </p>
                  )}
                  <pre className="border rounded-lg p-3 bg-muted/30 text-xs overflow-auto max-h-64">
                    {JSON.stringify(selectedLog.metadata, null, 2)}
                  </pre>
                </div>
              )}

              {selectedLog.metadata && (
                <div>
                  <p className="text-sm font-semibold mb-2">Metadata</p>
                  <pre className="border rounded-lg p-3 bg-muted/30 text-xs overflow-auto max-h-64">
                    {JSON.stringify(selectedLog.metadata, null, 2)}
                  </pre>
                </div>
              )}

              {selectedLog.ip_address && (
                <div>
                  <p className="text-muted-foreground text-xs">IP Address</p>
                  <p className="text-xs font-mono">{selectedLog?.ip_address}</p>
                </div>
              )}

              {selectedLog.user_agent && (
                <div>
                  <p className="text-muted-foreground text-xs">User Agent</p>
                  <p className="text-xs font-mono break-all">{selectedLog.user_agent}</p>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
